import * as React from 'react'
import { useNavigate, useParams } from 'react-router'
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { ConfirmationDto } from '@lib/dto/mcp.dto'
import type { McpConfirmationDecision } from '@lib/enums/mcp-confirmation-status.enum'
import type { McpPrincipalKind } from '@lib/enums/mcp-principal-kind.enum'
import { uuidParamSchema } from '@lib/validators/params'
import { useAuth } from '@/auth/AuthContext'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { AuthShell } from '@/components/AuthShell'
import { AppIdentity } from '@/components/connect/AppIdentity'
import { ErrorState, LoadingState } from '@/components/PageState'
import { RecordFacts } from '@/components/RecordSheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { isApiError } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { mcpKeys } from '@/hooks/queryKeys'
import { memberConsentService, staffConsentService } from '@/services/McpConsentService'

function serviceFor(who: McpPrincipalKind) {
  return who === 'staff' ? staffConsentService : memberConsentService
}

/** Signed-in accounts that can own this confirmation, in the order they are tried. */
function accountsFor(member: boolean, staff: boolean): McpPrincipalKind[] {
  const accounts: McpPrincipalKind[] = []
  if (member) accounts.push('tenant_user')
  if (staff) accounts.push('staff')
  return accounts
}

/** The confirmation, from whichever signed-in account it belongs to; null when it belongs to neither. */
async function findConfirmation(
  id: string,
  accounts: readonly McpPrincipalKind[],
): Promise<{ who: McpPrincipalKind; confirmation: ConfirmationDto } | null> {
  let lapsed: Error | null = null
  for (const who of accounts) {
    try {
      return { who, confirmation: await serviceFor(who).confirmation(id) }
    } catch (failure) {
      // Someone else's (or no such) confirmation answers 404, and an account
      // whose session has ended answers 401: either way, try the other account.
      if (!isApiError(failure) || (failure.status !== 404 && failure.status !== 401)) throw failure
      if (failure.status === 401) lapsed = failure
    }
  }
  // Nobody found it and a session had ended: that session may be the one it's for.
  if (lapsed) throw lapsed
  return null
}

function FullPageLoading() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-canvas">
      <LoadingState />
    </div>
  )
}

function SignInChoices({ from }: { from: string }) {
  const navigate = useNavigate()
  return (
    <div className="flex flex-col items-start gap-2">
      <Button onClick={() => void navigate('/login', { state: { from } })}>
        Sign in to your organization
      </Button>
      <Button variant="link" size="sm" onClick={() => void navigate('/ops/login', { state: { from } })}>
        PixelSign staff? Sign in here
      </Button>
    </div>
  )
}

/**
 * Where PixelSign asks the person behind an AI app to confirm a risky action
 * (a live send, a void, a staff change). Only the person whose connection
 * asked can see it, and only an explicit click answers it: opening the link
 * (or a link preview fetching it) never confirms anything.
 */
export function McpConfirmPage() {
  const { confirmationId: rawConfirmationId } = useParams()
  const parsedConfirmationId = uuidParamSchema.safeParse(rawConfirmationId)
  const confirmationId = parsedConfirmationId.success ? parsedConfirmationId.data : null
  const tenant = useTenantAuth()
  const staff = useAuth()
  const queryClient = useQueryClient()
  const accounts = accountsFor(tenant.isAuthenticated, staff.isAuthenticated)
  const query = useQuery({
    queryKey: mcpKeys.confirmation(confirmationId, accounts),
    queryFn:
      confirmationId !== null && accounts.length > 0 ? () => findConfirmation(confirmationId, accounts) : skipToken,
    retry: false,
  })
  const [pending, setPending] = React.useState<McpConfirmationDecision | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  if (confirmationId === null) {
    return (
      <AuthShell title="Confirm an action" size="md">
        <ErrorState message="That confirmation link is not valid." />
      </AuthShell>
    )
  }

  const from = `/mcp/confirm/${confirmationId}`

  if (tenant.isLoading || staff.isLoading) return <FullPageLoading />
  if (accounts.length === 0) {
    return (
      <AuthShell title="Confirm an action" description="Sign in with the account that connected the AI app to see what it’s asking." size="md">
        <SignInChoices from={from} />
      </AuthShell>
    )
  }
  if (query.isLoading) return <FullPageLoading />
  if (query.isError && isApiError(query.error) && query.error.status === 401) {
    return (
      <AuthShell title="Sign in again" description="Your session ended. Sign in with the account that connected the AI app to see what it’s asking." size="md">
        <SignInChoices from={from} />
      </AuthShell>
    )
  }
  if (query.isError) {
    return (
      <AuthShell title="Confirm an action" size="md">
        <ErrorState message={apiErrorMessage(query.error, 'Could not load this confirmation.')} />
      </AuthShell>
    )
  }
  if (!query.data) {
    return (
      <AuthShell title="This confirmation isn’t for this account" description="Sign in with the account that connected the AI app." size="md">
        <SignInChoices from={from} />
      </AuthShell>
    )
  }

  const { who, confirmation } = query.data
  const { app, summary } = confirmation
  // The API decides expiry. This browser's clock only warns, since it may be off.
  const expired = confirmation.status === 'expired'
  const mayHaveExpired = confirmation.status === 'pending' && new Date(confirmation.expiresAt) <= new Date()

  async function answer(decision: McpConfirmationDecision): Promise<void> {
    if (confirmationId === null) return
    setError(null)
    setPending(decision)
    try {
      const updated = await (decision === 'confirm' ? serviceFor(who).confirm(confirmationId) : serviceFor(who).decline(confirmationId))
      queryClient.setQueryData(mcpKeys.confirmation(confirmationId, accounts), { who, confirmation: updated })
    } catch (failure) {
      // Already answered (elsewhere) or expired: show where it stands now.
      setError(apiErrorMessage(failure, 'Could not record your answer.'))
      await query.refetch()
    } finally {
      setPending(null)
    }
  }

  if (confirmation.status === 'confirmed') {
    return <AuthShell title="Confirmed" description={`You can go back to ${app.name}.`} size="md">{null}</AuthShell>
  }
  if (confirmation.status === 'declined') {
    return <AuthShell title="Declined" description="Nothing was done." size="md">{null}</AuthShell>
  }
  if (expired) {
    return <AuthShell title="This request expired" description="Nothing was done. Ask again from your AI app if you still want this." size="md">{null}</AuthShell>
  }

  return (
    <AuthShell title={summary.action} size="md">
      <div className="space-y-4">
        <AppIdentity app={app} />
        {mayHaveExpired ? (
          <p className="text-caption text-muted-foreground">This request may have expired. If your answer isn’t recorded, ask again from {app.name}.</p>
        ) : null}
        <RecordFacts>
          {summary.organizationName ? (
            <>
              <dt className="text-muted-foreground">Organization</dt>
              <dd>{summary.organizationName}</dd>
            </>
          ) : null}
          {summary.mode ? (
            <>
              <dt className="text-muted-foreground">Mode</dt>
              <dd>{summary.mode === 'test' ? <Badge tone="warning">Test</Badge> : <Badge>Live</Badge>}</dd>
            </>
          ) : null}
          {summary.items.map((item) => (
            <React.Fragment key={item.label}>
              <dt className="text-muted-foreground">{item.label}</dt>
              <dd className="break-words">{item.value}</dd>
            </React.Fragment>
          ))}
        </RecordFacts>
        <p className="text-caption text-muted-foreground">
          Requested by {app.name} at {new Date(confirmation.createdAt).toLocaleString()}. If you didn’t ask for this, decline.
        </p>
        {error ? <ErrorState message={error} /> : null}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={pending !== null} onClick={() => void answer('decline')}>
            {pending === 'decline' ? <Loader2 className="animate-spin" /> : null}
            Decline
          </Button>
          <Button variant={summary.destructive ? 'destructive' : 'default'} disabled={pending !== null} onClick={() => void answer('confirm')}>
            {pending === 'confirm' ? <Loader2 className="animate-spin" /> : null}
            {summary.confirmLabel}
          </Button>
        </div>
      </div>
    </AuthShell>
  )
}
